
import { useEffect, useState } from 'react'

import { ApolloCache } from '@apollo/client'
import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle, ConfirmDialog, FplDialogTitle, LoadingBackdrop } from 'components/common'
import { useGetCurrentDriver } from 'components/suppliers/drivers/hooks'
import { Mode } from 'constants/index'
import {
  CreateAndUpdateDriverBidDtoInput,
  useCreateDriverBidMutation,
  useDeleteDriverBidMutation,
  useGetDriverBidForBookingQuery,
  useUpdateDriverBidMutation,
} from 'generated/graphql'


import { SubmitBidForm } from './SubmitBidForm'
import YourBookingBidFormSchema from './YourBookingBidFormSchema'

interface SubmitBidDialogProps {
  open: boolean
  bookingId: number | null
  onClose: () => void
}

const SubmitBidDialog = (props: SubmitBidDialogProps) => {
  const { open, bookingId, onClose } = props
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)

  const { data } = useGetCurrentDriver()

  const driverId = String(data?.driverProfile.id)

  const methods = useForm<CreateAndUpdateDriverBidDtoInput>({
    resolver: joiResolver(YourBookingBidFormSchema),
    mode: 'onChange',
    defaultValues: {
      price: '',
      currencyId: '',
      vehicleId: '',
      driverId,
      eta: new Date(),
      notes: '',
    },
  })

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
  } = methods

  const handleOnClose = () => {
    onClose()

    reset({
      price: '',
      currencyId: '',
      vehicleId: '',
      driverId,
      eta: new Date(),
      notes: '',
    })
  }

  const { data: driverBidForBookingData, loading } = useGetDriverBidForBookingQuery({
    fetchPolicy: 'network-only',
    variables: {
      id: Number(bookingId),
      where: {
        isAllocated: { eq: false },
      },
    },
    skip: !Number(bookingId),
  })

  const driverBidId = driverBidForBookingData?.driverBidForCurrentDriver?.id

  const modifyLoad = (cache: ApolloCache<any>, bidStatus: boolean) => {
    cache.modify({
      id: cache.identify({ __typename: 'AvailableLoadDto', id: bookingId }),
      fields: {
        hasDriverPlacedBid() {
          return bidStatus
        },
        isDriverBidActive() {
          return true
        },
      },
    })
  }

  const [createDriverBid, { loading: createDriverBidLoading }] = useCreateDriverBidMutation({
    onCompleted: () => {
      toast.success('Bid was submitted with success')
      handleOnClose()
    },
    update(cache) {
      modifyLoad(cache, true)
    },
  })

  const [updateDriverBid, { loading: updateDriverBidLoading }] = useUpdateDriverBidMutation({
    onCompleted: () => {
      toast.success('Bid was updated with success')
      handleOnClose()
    },
  })

  const [deleteDriverBid, { loading: deleteDriverBidLoading }] = useDeleteDriverBidMutation({
    onCompleted: () => {
      toast.success('Bid was deleted with success')
      handleOnClose()
    },
    update(cache) {
      modifyLoad(cache, false)
    },
  })

  const handleOnDelete = () => {
    setOpenConfirmDeleteModal(true)
  }

  const handleDeleteBid = async () => {
    if (!driverBidId) return

    await deleteDriverBid({
      variables: {
        id: Number(driverBidId),
      },
    })
  }

  const mode: number = driverBidId === undefined || driverBidId === null ? Mode.Create : Mode.Update

  const submitBid = (data: CreateAndUpdateDriverBidDtoInput) => {
    if (mode === Mode.Create) {
      createDriverBid({
        variables: {
          input: {
            bookingId: bookingId?.toString(),
            driverId,
            price: Number(data.price),
            currencyId: data.currencyId,
            vehicleId: data.vehicleId,
            eta: data.eta,
            notes: data.notes,
          },
        },
      })
    } else {
      updateDriverBid({
        variables: {
          input: {
            id: driverBidId!.toString(),
            bookingId: bookingId?.toString(),
            driverId,
            price: Number(data.price),
            currencyId: data.currencyId,
            vehicleId: data.vehicleId,
            eta: data.eta,
            notes: data.notes,
          },
        },
      })
    }
  }

  useEffect(() => {
    if (mode !== Mode.Update || !driverBidForBookingData?.driverBidForCurrentDriver || loading)
      return

    const { currencyId, driverId, eta, notes, price, vehicleId } =
      driverBidForBookingData.driverBidForCurrentDriver

    reset({
      currencyId: String(currencyId),
      driverId: String(driverId),
      eta: new Date(eta),
      notes: String(notes),
      price: String(price),
      vehicleId: String(vehicleId),
    })
  }, [mode, driverBidForBookingData?.driverBidForCurrentDriver, loading, reset])

  const isProcessing = createDriverBidLoading || updateDriverBidLoading || deleteDriverBidLoading
  const disabledSubmitBtn = !isValid || !isDirty || isProcessing
  const disabledDeleteBtn = !driverBidId || deleteDriverBidLoading

  return (
    <>
      <Dialog fullWidth maxWidth='md' open={open} aria-labelledby='form-submit-bid-dialog'>
        <FplDialogTitle id='form-user-driver-dialog-title' onClose={handleOnClose}>
          <CardTitle> Your Bid</CardTitle>
          <Typography variant='body1'>
            Please submit an amount and the vehicle you will use to perform the delivery.
          </Typography>
        </FplDialogTitle>

        <DialogContent>
          <FormProvider {...methods}>
            <SubmitBidForm mode={mode} />
          </FormProvider>
          <LoadingBackdrop loading={loading} />
        </DialogContent>

        <DialogActions>
          <Button variant='contained' disabled={isProcessing} color='grey' onClick={handleOnClose}>
            Close
          </Button>

          <Button
            variant='contained'
            disabled={disabledDeleteBtn}
            color='grey'
            onClick={handleOnDelete}>
            Delete
          </Button>

          <LoadingButton
            variant='contained'
            disabled={disabledSubmitBtn}
            loading={isProcessing}
            onClick={handleSubmit(submitBid)}>
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openConfirmDeleteModal}
        setOpen={setOpenConfirmDeleteModal}
        onConfirm={handleDeleteBid}
        title={'Are you sure you want to delete this Bid?'}
        message={
          'If you delete this Bid, you will need to submit a new one if you would like to take on this booking.'
        }
        option1='No, keep this Bid'
        option2='Yes, cancel this Bid'
      />
    </>
  )
}

export default SubmitBidDialog
